import {
  getOwner,
  onCleanup,
  onMount,
  runWithOwner,
  Show,
  splitProps,
  type ComponentProps,
  type JSXElement,
} from "solid-js";
import { Portal } from "solid-js/web";
import { IconButton } from "~/common/button";
import { registerLayerOrigin } from "~/common/floating";
import { Icon, type IconName } from "~/icon";
import { createScrollFade } from "~/util/scroll_fade";
import { assignRefs } from "~/util/solid";

import clsx from "~/util/html/style";

type ContentProps = Pick<ComponentProps<"div">, "style" | "class" | "children"> & {
  width?: "sm" | "md" | "lg" | "xl" | "fit";
  height?: "max-full";
  position?: "center" | "right";
};
type DialogContainerProps = Pick<
  ComponentProps<"dialog">,
  "ref" | "children" | "class" | "closedby"
> & {
  disableClickBackfill?: boolean;
  onClose?:
    | ((event: Event & { currentTarget: HTMLDialogElement; target: Element }) => void)
    | undefined;
  /** Enter confirms, shift+Enter doesn't, the same contract as PopoverConfirmContent. A focused
   *  button or link is skipped: Enter is already how you press the one you tabbed to. Setting
   *  this also keeps the browser's opening focus off the first button (usually Cancel), which
   *  would otherwise swallow Enter as a dismissal. */
  onEnter?: () => void;
  showCloseButton?: boolean;
  /** Where the dialog portals to; document.body by default. */
  mount?: Node;
};
type DialogProps = DialogContainerProps & {
  contentClass?: ContentProps["class"];
  contentStyle?: ContentProps["style"];
  width?: ContentProps["width"];
  height?: ContentProps["height"];
  position?: ContentProps["position"];
  title?: JSXElement;
  subtitle?: JSXElement;
  headerActions?: JSXElement;
  footerActions?: JSXElement;
  footerBanner?: JSXElement;
};
type ConfirmModalProps = {
  mount?: Node;
  message: JSXElement;
  onCancel: (confirm: false) => void;
  onConfirm: (confirm: true) => void;
};

// Marks every Solid modal dialog. Dialogs can stack (a dialog opened from inside another), so
// consumers must match all of them (e.g. closest()), never a single element by id.
export const DIALOG_MODAL_SELECTOR = "dialog[data-solid-modal]";

export const DIALOG_FADE_CX =
  "transition-opacity duration-250 not-open:opacity-0 starting:opacity-0";

// Dialogs can stack, so the scroll lock is reference-counted: only the first open dialog
// applies the styles and only the last one to close clears them.
let scrollLockCount = 0;

/**
 * @description Applies styles to the HTML document to lock body scroll while a dialog is open.
 * @returns A cleanup function when the dialog closes
 */
export function applyDocumentStyleForModal(): () => void {
  const { documentElement } = document;
  scrollLockCount += 1;
  if (scrollLockCount === 1) {
    Object.assign(documentElement.style, {
      // This is a calculation for the "width of a scrollbar" on OSs that have scrollbars. This
      // minor detail prevents layout shift when overflow is hidden. (It should be zero for OS
      // configurations without scrollbars).
      paddingRight: `${window.innerWidth - documentElement.clientWidth}px`,
      overflow: "hidden",
    });
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLockCount -= 1;
    if (scrollLockCount === 0) {
      Object.assign(documentElement.style, { paddingRight: "", overflow: "" });
    }
  };
}

// Escape is a user-agent close: it fires the dialog's own `cancel` event, and
// `closedby="none"` is the only attribute that refuses it. That attribute is not
// implemented in every browser yet, and where it is missing Escape still closes a
// dialog whose owner is holding it open through an in-flight submit — losing, in the
// upload flows, the only confirmation the user ever gets. Refusing the cancel event
// does the same job everywhere. The predicate is read at event time so a dialog that
// becomes refusable mid-life is covered too.
function createCancelRefusal(dialog: HTMLDialogElement, refused: () => boolean) {
  // Under the dialog's own owner: the compiler wraps a dynamic prop in a memo created
  // on read, and one created from a bare event handler has no owner to dispose it.
  const owner = getOwner();
  function refuse(event: Event) {
    if (runWithOwner(owner, refused)) event.preventDefault();
  }
  dialog.addEventListener("cancel", refuse);
  onCleanup(() => dialog.removeEventListener("cancel", refuse));
}

export function DialogContent(props: ContentProps) {
  const width = () => {
    // A drawer is narrow by default; `md` widens it for a detail pane that carries a form and a table.
    if (props.position === "right")
      return props.width === "md" ? "w-full max-w-[600px]" : "w-full max-w-[400px]";
    switch (props.width) {
      case "md":
        return "w-full max-w-2xl";
      case "lg":
        return "w-full max-w-4xl";
      case "xl":
        return "w-full max-w-7xl";
      case "fit":
        return "max-w-full";
      default:
        // sm
        return "w-full max-w-lg";
    }
  };
  const position = () =>
    props.position === "right"
      ? "ml-auto h-full max-h-full transition-transform duration-250 starting:translate-x-7"
      : "m-auto";
  return (
    <div
      class={clsx(
        props.class,
        "flex flex-col rounded-xl bg-white p-6 shadow-lg",
        props.height === "max-full" && "max-h-full",
        position(),
        width(),
      )}
      style={props.style}
    >
      {props.children}
    </div>
  );
}

// The open dialog fills the viewport and makes the rest of the page inert, so a press on a
// toast showing behind or above it arrives with the `<dialog />` as its target, exactly like a
// press on the backdrop, and hit testing (`elementFromPoint`) skips the toast. Only the
// coordinates tell the two apart. Each rendered toast carries `data-toast` (ToastManager).
function isOnToast(event: MouseEvent) {
  const { clientX: x, clientY: y } = event;
  return [...document.querySelectorAll("[data-toast]")].some((toast) => {
    const { left, right, top, bottom, width, height } = toast.getBoundingClientRect();
    return width > 0 && height > 0 && x >= left && x <= right && y >= top && y <= bottom;
  });
}

export function DialogContainer(props: DialogContainerProps) {
  let dialogRef: HTMLDialogElement | undefined;
  // Left where the dialog was rendered while the dialog portals out, so a popover it was opened
  // from counts a click in the dialog as inside (containsThroughLayers).
  const origin = document.createTextNode("");
  let mostRecentPointerDownTarget: Element | undefined;
  // Enter confirms once per open. A confirmation usually stays mounted until its action's
  // visit succeeds, so a second press in that window would start the action again; the
  // button is still there for a deliberate retry after a refusal.
  let enterFired = false;
  onMount(() => {
    registerLayerOrigin(dialogRef!, () => origin);
    dialogRef!.showModal();
    // showModal() lands focus on the first focusable descendant, which in a confirmation is
    // the Cancel button, so Enter would dismiss. A modal dialog is itself focusable, so it
    // takes the focus instead and the keydown below sees Enter.
    if (props.onEnter) {
      // Focusable without it per spec once modal, but the attribute makes that true everywhere.
      dialogRef!.tabIndex = -1;
      dialogRef!.focus();
    }
    createCancelRefusal(dialogRef!, () => props.closedby === "none");
    onCleanup(applyDocumentStyleForModal());
  });
  const portal = (
    <Portal mount={props.mount}>
      <dialog
        ref={(element) => {
          dialogRef = element;
          (props.ref as undefined | ((element: HTMLDialogElement) => void))?.(element);
        }}
        data-solid-modal
        onKeyDown={(event) => {
          if (!props.onEnter) return;
          // A held key repeats: the repeat after the Enter that opened the dialog would confirm
          // it unread, and each one after would fire the action again.
          if (event.key !== "Enter" || event.shiftKey || event.repeat) return;
          if (
            event.target instanceof HTMLButtonElement ||
            event.target instanceof HTMLAnchorElement
          )
            return;

          event.preventDefault();
          // A picker or confirm opened in the dialog mounts inside it; while one is up, Enter isn't the dialog's.
          if (dialogRef!.querySelector("[data-floating][data-open]")) return;
          if (enterFired) return;
          enterFired = true;
          props.onEnter();
        }}
        // Escape is a user-agent close, so only `closedby` can refuse it (backed by the
        // cancel listener above, for the browsers that ignore the attribute); the
        // backdrop closes through our own handler below, which `disableClickBackfill`
        // refuses. Hold a dialog open through an in-flight submit by setting both.
        closedby={props.closedby}
        onPointerDown={(event) => {
          mostRecentPointerDownTarget =
            event.target === dialogRef && isOnToast(event) ? undefined : event.target;
        }}
        onClose={(event) => props.onClose?.(event)}
        onClick={(event) => {
          // On backdrop click, close the modal. The backdrop is the only thing that
          // the `<dialog />` itself displays. We also check that the most recent
          // pointerdown target was the backdrop so that we know user didn't
          // accidentally "drag" off the modal. A press on a toast targets the `<dialog />`
          // too (see isOnToast) and is not a backdrop click.
          if (
            !props.disableClickBackfill &&
            event.target === dialogRef &&
            event.target === mostRecentPointerDownTarget &&
            !isOnToast(event)
          ) {
            dialogRef.close();
          }
        }}
        class={clsx(
          "isolate flex h-dvh max-h-none w-dvw max-w-full bg-transparent p-5 outline-none backdrop:bg-black/50",
          DIALOG_FADE_CX,
          props.class,
        )}
      >
        {props.children}
      </dialog>
    </Portal>
  );
  return [origin, portal];
}

export function Dialog(props: DialogProps) {
  let dialogRef!: HTMLDialogElement;

  return (
    <DialogContainer
      ref={assignRefs((el) => (dialogRef = el), props.ref)}
      class={props.class}
      onClose={props.onClose}
      onEnter={props.onEnter}
      closedby={props.closedby}
      disableClickBackfill={props.disableClickBackfill}
      mount={props.mount}
    >
      <DialogContent
        class={props.contentClass}
        style={props.contentStyle}
        width={props.width}
        height={props.height}
        position={props.position}
      >
        <div class="mb-4 flex items-center justify-between gap-4 empty:hidden">
          {/* Inside the Show so a title-less dialog leaves the row genuinely empty for empty:hidden. */}
          <Show when={props.title}>
            {(title) => (
              <div class="flex flex-col gap-1">
                <h1 class="text-xl font-bold">{title()}</h1>
                {/* Wrapped: a fragment subtitle would splice into this column and stack. */}
                <Show when={props.subtitle}>
                  <div class="text-sm text-typography-secondary">{props.subtitle}</div>
                </Show>
              </div>
            )}
          </Show>
          <Show when={props.headerActions || props.showCloseButton}>
            <div class="mb-auto ml-auto flex items-center gap-3">
              {props.headerActions}
              <Show when={props.showCloseButton}>
                <IconButton
                  variant="plain"
                  iconName="x"
                  aria-label="Close"
                  onClick={() => dialogRef.close()}
                  size="lg"
                />
              </Show>
            </div>
          </Show>
        </div>

        {props.children}

        <Show when={props.footerBanner}>
          <div class="mt-6">{props.footerBanner}</div>
        </Show>

        <Show when={props.footerActions}>
          <DialogFooter>{props.footerActions}</DialogFooter>
        </Show>
      </DialogContent>
    </DialogContainer>
  );
}

export function ConfirmModal(props: ConfirmModalProps) {
  const handleCancel = () => props.onCancel(false);
  return (
    <Dialog mount={props.mount} onClose={handleCancel} onEnter={() => props.onConfirm(true)}>
      <h1 class="text-lg font-semibold">{props.message}</h1>
      <DialogFooter>
        <button
          type="button"
          class="rounded-full border border-brand-subtle px-5 py-2 text-base font-medium whitespace-nowrap text-typography-primary transition ease-in-out hover:border-brand-secondary hover:bg-brand-light"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          class="rounded-full border border-brand-danger px-5 py-2 text-base font-medium whitespace-nowrap transition ease-in-out hover:bg-brand-danger-light"
          onClick={() => props.onConfirm(true)}
        >
          Confirm
        </button>
      </DialogFooter>
    </Dialog>
  );
}

export function DialogFooter(props: { children: JSXElement }) {
  return <footer class="mt-6 flex items-center justify-end gap-4">{props.children}</footer>;
}

// ScrollableCardContent's dialog twin: the scrollable body of a long dialog — pair with `height="max-full"` on
// Dialog so the content column caps at the viewport and this region scrolls.
// Padding is pulled inside the scroll box
// (p-1/-m-1) so inputs' focus rings render instead of clipping at the
// container's edges, and the bottom fades while more content is below.
export function ScrollableDialogContent(props: ComponentProps<"div">) {
  const [own, rest] = splitProps(props, ["class", "children", "ref"]);
  let scrollRef: HTMLDivElement | undefined;
  createScrollFade(() => scrollRef, { positions: ["bottom"] });

  return (
    <div
      {...rest}
      ref={assignRefs((element) => (scrollRef = element), own.ref)}
      class={clsx("-m-1 min-h-0 overflow-y-auto p-1", own.class)}
    >
      {own.children}
    </div>
  );
}

type FullScreenDialogProps = {
  children?: JSXElement;
  onClose?: () => void;
};

export function FullScreenDialog(props: FullScreenDialogProps) {
  let dialogRef: HTMLDialogElement | undefined;

  onMount(() => {
    dialogRef!.showModal();
    // Withholding onClose is how a caller holds this dialog open, so it is also what
    // the Escape refusal keys on.
    createCancelRefusal(dialogRef!, () => !props.onClose);
    onCleanup(applyDocumentStyleForModal());
  });

  return (
    <Portal>
      <dialog
        ref={(element) => {
          dialogRef = element;
        }}
        closedby={props.onClose ? undefined : "none"}
        onClose={() => {
          props.onClose?.();
        }}
        class={clsx(
          "isolate flex h-dvh max-h-none w-dvw max-w-none flex-col bg-white p-0",
          DIALOG_FADE_CX,
        )}
      >
        {props.children}
      </dialog>
    </Portal>
  );
}

type FullScreenDialogHeaderProps = {
  leadingIcon?: IconName;
  title: JSXElement;
  subtitle?: JSXElement;
  actions?: JSXElement;
  onClose?: () => void;
};

export function FullScreenDialogHeader(props: FullScreenDialogHeaderProps) {
  return (
    <header class="flex shrink-0 items-center gap-3 border-b border-brand-subtle px-4 py-3">
      <Show when={props.leadingIcon}>
        {(iconName) => <Icon name={iconName()} class="shrink-0 text-typography-tertiary" />}
      </Show>

      <div class="flex min-w-0 flex-1 flex-col">
        <div class="min-w-0 text-sm font-medium text-typography-primary">{props.title}</div>

        <Show when={props.subtitle}>
          <div class="truncate text-xs text-typography-secondary">{props.subtitle}</div>
        </Show>
      </div>

      <Show when={props.actions}>
        {(actions) => <div class="flex items-center gap-2">{actions()}</div>}
      </Show>

      <Show when={props.onClose}>
        {(onClose) => (
          <IconButton
            variant="plain"
            iconName="x"
            size="lg"
            onClick={onClose()}
            aria-label="Close"
          />
        )}
      </Show>
    </header>
  );
}
